import { userService } from './user-service';
import { languageService } from './language-service';

// Use the supabase client from the dedicated file
import { supabase } from './supabase-client';

// Generate a unique session ID for this browser session
const generateSessionId = () => {
  return Math.random().toString(36).substring(2, 15);
};

class LoggingService {
  constructor() {
    this.sessionId = generateSessionId();
    this.userId = userService.getUserId();
    this.language = languageService.getLanguage();
    
    // Store user language preference
    this.storeLanguagePreference();
    
    // Log session start
    this.logSessionStart();
  }
  
  /**
   * Store user language preference in Supabase
   */
  async storeLanguagePreference() {
    try {
      const { error } = await supabase
        .from('user_language_preferences')
        .upsert({
          user_id: this.userId,
          language: this.language,
          auto_detected: true
        }, { onConflict: 'user_id' });
        
      if (error) {
        console.error('Error storing language preference:', error);
      }
    } catch (error) {
      console.error('Failed to store language preference:', error);
    }
  }
  
  /**
   * Update user's preferred language
   * @param {string} languageCode - ISO language code
   */
  async updateLanguage(languageCode) {
    this.language = languageCode;
    languageService.setLanguage(languageCode);
    
    try {
      const { error } = await supabase
        .from('user_language_preferences')
        .upsert({
          user_id: this.userId,
          language: languageCode,
          auto_detected: false
        }, { onConflict: 'user_id' });
        
      if (error) {
        console.error('Error updating language preference:', error);
      }
    } catch (error) {
      console.error('Failed to update language preference:', error);
    }
  }
  
  /**
   * Log session start to Supabase
   */
  async logSessionStart() {
    try {
      const deviceInfo = {
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language
      };
      
      const { error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: this.userId,
          device_info: deviceInfo
        });
        
      if (error) {
        console.error('Error logging session start:', error);
      }
    } catch (error) {
      console.error('Failed to log session start:', error);
    }
  }
  
  /**
   * Log user interaction to Supabase
   * @param {string} eventType - Type of interaction
   * @param {string} message - User message or system response
   * @param {string} prompt - Prompt used for generation
   * @param {Object} metadata - Additional interaction metadata
   */
  async logInteraction(eventType, message, prompt, metadata = {}) {
    try {
      const interactionRecord = {
        session_id: this.sessionId,
        event_type: eventType,
        message: message || null,
        prompt: prompt || null,
        metadata: {
          language: this.language,
          user_id: this.userId,
          ...metadata
        }
      };

      const { error } = await supabase
        .from('user_interactions')
        .insert(interactionRecord);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error logging interaction:', error);
    }
  }
}

// Create and export a single instance
export const loggingService = new LoggingService();

// Export the loggingService as interactionService for language updates and other functionality
export const interactionService = loggingService;
